import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useHelps from "../hooks/useHelps";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import TvIcon from "@material-ui/icons/Tv";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CodeRoundedIcon from "@material-ui/icons/CodeRounded";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import FlagOutlinedIcon from "@material-ui/icons/FlagOutlined";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import ViewKanban from "@mui/icons-material/ViewKanban";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import TravelExplore from "@mui/icons-material/TravelExplore";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import AssignmentIcon from "@mui/icons-material/Assignment";
import Schedule from "@material-ui/icons/Schedule";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PeopleIcon from "@material-ui/icons/People";
import ListIcon from "@material-ui/icons/ListAlt";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import ForumIcon from "@material-ui/icons/Forum";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import BusinessIcon from "@material-ui/icons/Business";
import EventNoteIcon from "@material-ui/icons/EventNote";
import {
  AllInclusive,
  AttachFile,
  Dashboard,
  Description,
  DeviceHubOutlined,
  GridOn,
  ListAlt,
  PhonelinkSetup,
} from "@material-ui/icons";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";

import { Can } from "../components/Can";

import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import useVersion from "../hooks/useVersion";
import { i18n } from "../translate/i18n";
import { Campaign, ShapeLine, Webhook, Psychology } from "@mui/icons-material";
import { hasPermission, hasAnyPermission } from "../helpers/hasPermission";

const useStyles = makeStyles((theme) => ({
  listItem: {
    height: "44px",
    width: "auto",
    margin: theme.spacing(0.25, 1),
    borderRadius: theme.shape.borderRadius,
    transition: "background-color .12s ease-in-out",
    "&:hover $iconHoverActive": {
      color: theme.palette.primary.main,
    },
  },

  listItemActive: {
    backgroundColor: theme.mode === "light"
      ? `${theme.palette.primary.main}14`
      : `${theme.palette.primary.main}26`,
  },

  listItemText: {
    fontSize: "14px",
    color: theme.mode === "light" ? "#666" : "#FFF",
  },

  listItemTextActive: {
    fontSize: "14px",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  avatarActive: {
    backgroundColor: "transparent",
  },
  avatarHover: {
    backgroundColor: "transparent",
  },
  iconHoverActive: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 28,
    width: 28,
    backgroundColor: "transparent",
    color: theme.mode === "light" ? "#666" : "#FFF",
    opacity: 0.75,
    "&:hover, &.active": {
      backgroundColor: "transparent",
      color: theme.palette.primary.main,
      opacity: 1,
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.4rem",
    },
  },
}));

function ListItemLink(props) {
  const { icon, primary, to, tooltip, showBadge } = props;
  const classes = useStyles();
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const isActive = activeMenu === to || location.pathname === to;

  const renderLink = React.useMemo(
    () => React.forwardRef((itemProps, ref) => <RouterLink to={to} ref={ref} {...itemProps} />),
    [to]
  );

  const ConditionalTooltip = ({ children, tooltipEnabled }) =>
    tooltipEnabled ? (
      <Tooltip title={primary} placement="right">
        {children}
      </Tooltip>
    ) : (
      children
    );

  return (
    <ConditionalTooltip tooltipEnabled={!!tooltip}>
      <li>
        <ListItem
          button
          component={renderLink}
          className={`${classes.listItem} ${isActive ? classes.listItemActive : ""}`}
        >
          {icon ? (
            <ListItemIcon>
              {showBadge ? (
                <Badge badgeContent="!" color="error" overlap="circular" className={classes.badge}>
                  <Avatar className={`${classes.iconHoverActive} ${isActive ? "active" : ""}`}>{icon}</Avatar>
                </Badge>
              ) : (
                <Avatar className={`${classes.iconHoverActive} ${isActive ? "active" : ""}`}>{icon}</Avatar>
              )}
            </ListItemIcon>
          ) : null}
          <ListItemText
            primary={
              <Typography className={isActive ? classes.listItemTextActive : classes.listItemText}>
                {primary}
              </Typography>
            }
          />
        </ListItem>
      </li>
    </ConditionalTooltip>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const MainListItems = ({ collapsed, drawerClose }) => {
  const theme = useTheme();
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);
  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [openCampaignSubmenu, setOpenCampaignSubmenu] = useState(false);
  const [openFlowSubmenu, setOpenFlowSubmenu] = useState(false);
  // Pedido do usuario 2026-10-08 (reformulacao do menu): so um grupo de
  // navegacao principal fica aberto por vez - abrir um recolhe
  // automaticamente qualquer outro que estivesse aberto. Os
  // mini-acordeoes internos (Campanhas/Flowbuilder dentro de "Campanhas
  // & Automacao") continuam independentes, ver openCampaignSubmenu/
  // openFlowSubmenu acima.
  const [openGroup, setOpenGroup] = useState(null);
  const toggleGroup = (name) => setOpenGroup((prev) => (prev === name ? null : name));
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);

  // novas features
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [version, setVersion] = useState(false);
  const [managementHover, setManagementHover] = useState(false);
  const [campaignHover, setCampaignHover] = useState(false);
  const [flowHover, setFlowHover] = useState(false)
  const { list } = useHelps();
  const [hasHelps, setHasHelps] = useState(false);


  useEffect(() => {
    async function checkHelps() {
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
  }, []);

  const isManagementActive =
    location.pathname === "/" || location.pathname.startsWith("/reports") || location.pathname.startsWith("/moments");

  const isCampaignRouteActive =
    location.pathname === "/campaigns" ||
    location.pathname.startsWith("/contact-lists") ||
    location.pathname.startsWith("/campaigns-config");

  const isFlowbuilderRouteActive = 
    location.pathname.startsWith("/phrase-lists") ||
    location.pathname.startsWith("/flowbuilders");

  useEffect(() => {
    if (location.pathname.startsWith("/tickets")) {
      setActiveMenu("/tickets");
    } else {
      setActiveMenu("");
    }
  }, [location, setActiveMenu]);

  const { getPlanCompany } = usePlans();

  const { getVersion } = useVersion();

  useEffect(() => {
    async function fetchVersion() {
      const _version = await getVersion();
      setVersion(_version.version);
    }
    fetchVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);

      setShowCampaigns(planConfigs.plan.useCampaigns);
      setShowKanban(planConfigs.plan.useKanban);
      setShowOpenAi(planConfigs.plan.useOpenAi);
      setShowIntegrations(planConfigs.plan.useIntegrations);
      setShowSchedules(planConfigs.plan.useSchedules);
      setShowInternalChat(planConfigs.plan.useInternalChat);
      setShowExternalApi(planConfigs.plan.useExternalApi);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id) {
      const companyId = user.companyId;
      //    const socket = socketManager.GetSocket();
      // console.log('socket nListItems')
      const onCompanyChatMainListItems = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      socket.on(`company-${companyId}-chat`, onCompanyChatMainListItems);
      return () => {
        socket.off(`company-${companyId}-chat`, onCompanyChatMainListItems);
      };
    }
  }, [socket]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div onClick={drawerClose}>
      {/* Pedido do usuario 2026-10-08: "Atendimentos" fica sozinho, fora
          de qualquer grupo, sempre o primeiro item do menu. */}
      <ListItemLink
        to="/tickets"
        primary={i18n.t("mainDrawer.listItems.tickets")}
        icon={<WhatsAppIcon />}
        tooltip={collapsed}
      />

      <Can
        role={
          (user.profile === "user" && user.showDashboard === "enabled") || user.allowRealTime === "enabled"
            ? "admin"
            : user.profile
        }
        perform={"drawer-admin-items:view"}
        yes={() => (
          <>
            <Tooltip title={collapsed ? i18n.t("mainDrawer.listItems.management") : ""} placement="right">
              <ListItem
                dense
                button
                onClick={() => toggleGroup("gerencia")}
                onMouseEnter={() => setManagementHover(true)}
                onMouseLeave={() => setManagementHover(false)}
              >
                <ListItemIcon>
                  <Avatar
                    className={`${classes.iconHoverActive} ${isManagementActive || managementHover ? "active" : ""}`}
                  >
                    <Dashboard />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography className={classes.listItemText}>
                      {i18n.t("mainDrawer.listItems.management")}
                    </Typography>
                  }
                />
                {openGroup === "gerencia" ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
            </Tooltip>
            <Collapse
              in={openGroup === "gerencia"}
              timeout="auto"
              unmountOnExit
              style={{
                backgroundColor: theme.mode === "light" ? "rgba(120,120,120,0.1)" : "rgba(120,120,120,0.5)",
              }}
            >
              <Can
                role={user.profile === "user" && user.showDashboard === "enabled" ? "admin" : user.profile}
                perform={"drawer-admin-items:view"}
                yes={() => (
                  <>
                    <ListItemLink
                      small
                      to="/"
                      primary="Dashboard"
                      icon={<DashboardOutlinedIcon />}
                      tooltip={collapsed}
                    />
                    <ListItemLink
                      small
                      to="/painel-tv"
                      primary="Painel TV"
                      icon={<TvIcon />}
                      tooltip={collapsed}
                    />
                    <ListItemLink
                      small
                      to="/reports"
                      primary={i18n.t("mainDrawer.listItems.reports")}
                      icon={<Description />}
                      tooltip={collapsed}
                    />
                  </>
                )}
              />
              <Can
                role={user.profile === "user" && user.allowRealTime === "enabled" ? "admin" : user.profile}
                perform={"drawer-admin-items:view"}
                yes={() => (
                  <ListItemLink
                    to="/moments"
                    primary={i18n.t("mainDrawer.listItems.chatsTempoReal")}
                    icon={<GridOn />}
                    tooltip={collapsed}
                  />
                )}
              />
              {/* Pedido do usuario 2026-10-09: tela de controle de
                  sobreaviso/plantao, dentro de Gerencia. */}
              <Can
                role={(hasPermission(user, "menu:on-call") || hasPermission(user, "oncall:calendar-view")) ? "admin" : user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/on-call"
                    primary="Sobreaviso"
                    icon={<EventNoteIcon />}
                    tooltip={collapsed}
                  />
                )}
              />
            </Collapse>
          </>
        )}
      />

      {/* Grupo "Produtividade" - pedido do usuario 2026-10-08. */}
      <Tooltip title={collapsed ? "Produtividade" : ""} placement="right">
        <ListItem dense button onClick={() => toggleGroup("produtividade")}>
          <ListItemIcon>
            <Avatar className={`${classes.iconHoverActive} ${openGroup === "produtividade" ? "active" : ""}`}>
              <FlashOnIcon />
            </Avatar>
          </ListItemIcon>
          <ListItemText primary={<Typography className={classes.listItemText}>Produtividade</Typography>} />
          {openGroup === "produtividade" ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItem>
      </Tooltip>
      <Collapse
        in={openGroup === "produtividade"}
        timeout="auto"
        unmountOnExit
        style={{
          backgroundColor: theme.mode === "light" ? "rgba(120,120,120,0.1)" : "rgba(120,120,120,0.5)",
        }}
      >
        <List dense component="div" disablePadding>
          <ListItemLink
            to="/quick-messages"
            primary={i18n.t("mainDrawer.listItems.quickMessages")}
            icon={<FlashOnIcon />}
            tooltip={collapsed}
          />
          {showKanban && (
            <Can
              role={hasPermission(user, "menu:boards") ? "admin" : user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/boards"
                  primary={i18n.t("mainDrawer.listItems.kanban")}
                  icon={<ViewKanban />}
                  tooltip={collapsed}
                />
              )}
            />
          )}
          {showKanban && (
            <Can
              role={hasPermission(user, "menu:tasks") ? "admin" : user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/tasks"
                  primary="Tarefas"
                  icon={<AssignmentIcon />}
                  tooltip={collapsed}
                />
              )}
            />
          )}
          {showKanban && (
            <Can
              role={hasPermission(user, "menu:clientes-criticos") ? "admin" : user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/clientes-criticos"
                  primary="Clientes Críticos"
                  icon={<ReportProblemIcon />}
                  tooltip={collapsed}
                />
              )}
            />
          )}
          {showInternalChat && (
            <ListItemLink
              to="/chats"
              primary={i18n.t("mainDrawer.listItems.chats")}
              icon={
                <Badge color="secondary" variant="dot" invisible={invisible}>
                  <ForumIcon />
                </Badge>
              }
              tooltip={collapsed}
            />
          )}
          {showSchedules && (
            <Can
              role={hasPermission(user, "menu:schedules") ? "admin" : user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/schedules"
                  primary={i18n.t("mainDrawer.listItems.schedules")}
                  icon={<Schedule />}
                  tooltip={collapsed}
                />
              )}
            />
          )}
          {showKanban && (
            <Can
              role={hasPermission(user, "menu:commercial") ? "admin" : user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/commercial"
                  primary="Comercial"
                  icon={<TravelExplore />}
                  tooltip={collapsed}
                />
              )}
            />
          )}
        </List>
      </Collapse>

      {/* Grupo "Contatos & CRM" - pedido do usuario 2026-10-08. */}
      <Tooltip title={collapsed ? "Contatos & CRM" : ""} placement="right">
        <ListItem dense button onClick={() => toggleGroup("contatos")}>
          <ListItemIcon>
            <Avatar className={`${classes.iconHoverActive} ${openGroup === "contatos" ? "active" : ""}`}>
              <ContactPhoneOutlinedIcon />
            </Avatar>
          </ListItemIcon>
          <ListItemText primary={<Typography className={classes.listItemText}>Contatos &amp; CRM</Typography>} />
          {openGroup === "contatos" ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItem>
      </Tooltip>
      <Collapse
        in={openGroup === "contatos"}
        timeout="auto"
        unmountOnExit
        style={{
          backgroundColor: theme.mode === "light" ? "rgba(120,120,120,0.1)" : "rgba(120,120,120,0.5)",
        }}
      >
        <List dense component="div" disablePadding>
          <ListItemLink
            to="/contacts"
            primary={i18n.t("mainDrawer.listItems.contacts")}
            icon={<ContactPhoneOutlinedIcon />}
            tooltip={collapsed}
          />
          <ListItemLink
            to="/tags"
            primary={i18n.t("mainDrawer.listItems.tags")}
            icon={<LocalOfferIcon />}
            tooltip={collapsed}
          />
        </List>
      </Collapse>

      <Can
        role={
          user.profile === "user" && (user.allowConnections === "enabled" || hasAnyPermission(user))
            ? "admin"
            : user.profile
        }
        perform="dashboard:view"
        yes={() => (
          <>
            <Divider />
            <ListSubheader inset>{i18n.t("mainDrawer.listItems.administration")}</ListSubheader>

            {/* Grupo "Campanhas & Automação" - pedido do usuario 2026-10-08:
                Campanhas, Flowbuilder e Informativos juntos num grupo so,
                cada um mantendo seu proprio mini-acordeao interno. */}
            <Tooltip title={collapsed ? "Campanhas & Automação" : ""} placement="right">
              <ListItem dense button onClick={() => toggleGroup("campanhasAutomacao")}>
                <ListItemIcon>
                  <Avatar className={`${classes.iconHoverActive} ${openGroup === "campanhasAutomacao" ? "active" : ""}`}>
                    <Campaign />
                  </Avatar>
                </ListItemIcon>
                <ListItemText primary={<Typography className={classes.listItemText}>Campanhas &amp; Automação</Typography>} />
                {openGroup === "campanhasAutomacao" ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
            </Tooltip>
            <Collapse
              in={openGroup === "campanhasAutomacao"}
              timeout="auto"
              unmountOnExit
              style={{
                backgroundColor: theme.mode === "light" ? "rgba(120,120,120,0.1)" : "rgba(120,120,120,0.5)",
              }}
            >
              <List dense component="div" disablePadding>
                {showCampaigns && (
                  <Can
                    role={hasPermission(user, "menu:campaigns") ? "admin" : user.profile}
                    perform="dashboard:view"
                    yes={() => (
                      <>
                        <Tooltip title={collapsed ? i18n.t("mainDrawer.listItems.campaigns") : ""} placement="right">
                          <ListItem
                            dense
                            button
                            onClick={() => setOpenCampaignSubmenu((prev) => !prev)}
                            onMouseEnter={() => setCampaignHover(true)}
                            onMouseLeave={() => setCampaignHover(false)}
                          >
                            <ListItemIcon>
                              <Avatar
                                className={`${classes.iconHoverActive} ${isCampaignRouteActive || campaignHover ? "active" : ""
                                  }`}
                              >
                                <EventAvailableIcon />
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography className={classes.listItemText}>
                                  {i18n.t("mainDrawer.listItems.campaigns")}
                                </Typography>
                              }
                            />
                            {openCampaignSubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </ListItem>
                        </Tooltip>
                        <Collapse
                          in={openCampaignSubmenu}
                          timeout="auto"
                          unmountOnExit
                          style={{
                            backgroundColor: theme.mode === "light" ? "rgba(120,120,120,0.1)" : "rgba(120,120,120,0.5)",
                          }}
                        >
                          <List dense component="div" disablePadding>
                            <ListItemLink
                              to="/campaigns"
                              primary={i18n.t("campaigns.subMenus.list")}
                              icon={<ListIcon />}
                              tooltip={collapsed}
                            />
                            <ListItemLink
                              to="/contact-lists"
                              primary={i18n.t("campaigns.subMenus.listContacts")}
                              icon={<PeopleIcon />}
                              tooltip={collapsed}
                            />
                            <ListItemLink
                              to="/campaigns-config"
                              primary={i18n.t("campaigns.subMenus.settings")}
                              icon={<SettingsOutlinedIcon />}
                              tooltip={collapsed}
                            />
                          </List>
                        </Collapse>
                      </>
                    )}
                  />
                )}

                {/* FLOWBUILDER */}
                <Can
                  role={hasPermission(user, "menu:flowbuilder") ? "admin" : user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <>
                      <Tooltip title={collapsed ? i18n.t("mainDrawer.listItems.flowbuilder") : ""} placement="right">
                        <ListItem
                          dense
                          button
                          onClick={() => setOpenFlowSubmenu((prev) => !prev)}
                          onMouseEnter={() => setFlowHover(true)}
                          onMouseLeave={() => setFlowHover(false)}
                        >
                          <ListItemIcon>
                            <Avatar
                              className={`${classes.iconHoverActive} ${isFlowbuilderRouteActive || flowHover ? "active" : ""
                                }`}
                            >
                              <Webhook />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography className={classes.listItemText}>
                                {i18n.t("mainDrawer.listItems.flowbuilder")}
                              </Typography>
                            }
                          />
                          {openFlowSubmenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </ListItem>
                      </Tooltip>

                      <Collapse
                        in={openFlowSubmenu}
                        timeout="auto"
                        unmountOnExit
                        style={{
                          backgroundColor: theme.mode === "light" ? "rgba(120,120,120,0.1)" : "rgba(120,120,120,0.5)",
                        }}
                      >
                        <List dense component="div" disablePadding>
                          <ListItemLink
                            to="/phrase-lists"
                            primary={i18n.t("flowbuilder.subMenus.campaign")}
                            icon={<EventAvailableIcon />}
                            tooltip={collapsed}
                          />

                          <ListItemLink
                            to="/flowbuilders"
                            primary={i18n.t("flowbuilder.subMenus.conversation")}
                            icon={<ShapeLine />}
                            tooltip={collapsed}
                          />
                        </List>
                      </Collapse>
                    </>
                  )}
                />

                {user.super && (
                  <ListItemLink
                    to="/announcements"
                    primary={i18n.t("mainDrawer.listItems.annoucements")}
                    icon={<AnnouncementIcon />}
                    tooltip={collapsed}
                  />
                )}
              </List>
            </Collapse>

            {/* Grupo "IA" - pedido do usuario 2026-09-11 (B7 da auditoria de
                telas): agrupar itens soltos do menu Administracao por tema
                em vez de lista unica de 18 itens. */}
            <Tooltip title={collapsed ? "IA & Conhecimento" : ""} placement="right">
              <ListItem
                dense
                button
                onClick={() => toggleGroup("ia")}
              >
                <ListItemIcon>
                  <Avatar className={`${classes.iconHoverActive} ${openGroup === "ia" ? "active" : ""}`}>
                    <Psychology />
                  </Avatar>
                </ListItemIcon>
                <ListItemText primary={<Typography className={classes.listItemText}>IA &amp; Conhecimento</Typography>} />
                {openGroup === "ia" ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
            </Tooltip>
            <Collapse
              in={openGroup === "ia"}
              timeout="auto"
              unmountOnExit
              style={{
                backgroundColor: theme.mode === "light" ? "rgba(120,120,120,0.1)" : "rgba(120,120,120,0.5)",
              }}
            >
              <List dense component="div" disablePadding>
                {/* Pedido do usuario 2026-10-08: "Base de Conhecimento" e
                    "Lista de Arquivos" eram duas telas desconectadas -
                    unificadas em /knowledge-base (abas: Perguntas e
                    Respostas / Arquivos). Link unico aqui, visivel se o
                    usuario tiver permissao pra qualquer uma das duas. */}
                <Can
                  role={
                    hasPermission(user, "menu:faq-moderation") || hasPermission(user, "menu:files")
                      ? "admin"
                      : user.profile
                  }
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/knowledge-base"
                      primary="Base de Conhecimento"
                      icon={<LibraryBooksIcon />}
                      tooltip={collapsed}
                    />
                  )}
                />
                <Can
                  role={hasPermission(user, "menu:ai-costs") ? "admin" : user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/ai-costs"
                      primary={i18n.t("mainDrawer.listItems.aiCosts")}
                      icon={<AttachMoneyIcon />}
                      tooltip={collapsed}
                    />
                  )}
                />
                {showOpenAi && (
                  <Can
                    role={hasPermission(user, "menu:prompts") ? "admin" : user.profile}
                    perform="dashboard:view"
                    yes={() => (
                      <ListItemLink
                        to="/prompts"
                        primary={i18n.t("mainDrawer.listItems.prompts")}
                        icon={<AllInclusive />}
                        tooltip={collapsed}
                      />
                    )}
                  />
                )}
                <Can
                  role={user.profile === "user" && user.allowPendingConfirmation === "enabled" ? "admin" : user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/pending-confirmations"
                      primary="Confirmação de Pendências"
                      icon={<FlagOutlinedIcon />}
                      tooltip={collapsed}
                    />
                  )}
                />
              </List>
            </Collapse>

            {/* Grupo "Configurações" - pedido do usuario 2026-10-08: Sistema,
                Conexões, Operacional e Empresas juntos num grupo so, cada
                um como um sub-bloco com rotulo (nao clicavel), em vez de 4
                secoes separadas soltas no menu. */}
            <Tooltip title={collapsed ? "Configurações" : ""} placement="right">
              <ListItem dense button onClick={() => toggleGroup("configuracoes")}>
                <ListItemIcon>
                  <Avatar className={`${classes.iconHoverActive} ${openGroup === "configuracoes" ? "active" : ""}`}>
                    <SettingsOutlinedIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText primary={<Typography className={classes.listItemText}>Configurações</Typography>} />
                {openGroup === "configuracoes" ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
            </Tooltip>
            <Collapse
              in={openGroup === "configuracoes"}
              timeout="auto"
              unmountOnExit
              style={{
                backgroundColor: theme.mode === "light" ? "rgba(120,120,120,0.1)" : "rgba(120,120,120,0.5)",
              }}
            >
              <List dense component="div" disablePadding>
                <ListSubheader disableSticky style={{ fontSize: 11, lineHeight: "28px" }}>Sistema</ListSubheader>
                <Can
                  role={hasPermission(user, "menu:users") ? "admin" : user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/users"
                      primary={i18n.t("mainDrawer.listItems.users")}
                      icon={<PeopleAltOutlinedIcon />}
                      tooltip={collapsed}
                    />
                  )}
                />
                <Can
                  role={hasPermission(user, "menu:queues") ? "admin" : user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/queues"
                      primary={i18n.t("mainDrawer.listItems.queues")}
                      icon={<AccountTreeOutlinedIcon />}
                      tooltip={collapsed}
                    />
                  )}
                />
                <Can
                  role={hasPermission(user, "menu:financeiro") ? "admin" : user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/financeiro"
                      primary={i18n.t("mainDrawer.listItems.financeiro")}
                      icon={<LocalAtmIcon />}
                      tooltip={collapsed}
                    />
                  )}
                />
                <Can
                  role={hasPermission(user, "menu:settings") ? "admin" : user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/settings"
                      primary={i18n.t("mainDrawer.listItems.settings")}
                      icon={<SettingsOutlinedIcon />}
                      tooltip={collapsed}
                    />
                  )}
                />

                <ListSubheader disableSticky style={{ fontSize: 11, lineHeight: "28px" }}>Conexões</ListSubheader>
                <Can
                  role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
                  perform={"drawer-admin-items:view"}
                  yes={() => (
                    <ListItemLink
                      to="/connections"
                      primary={i18n.t("mainDrawer.listItems.connections")}
                      icon={<SyncAltIcon />}
                      showBadge={connectionWarning}
                      tooltip={collapsed}
                    />
                  )}
                />
                {showIntegrations && (
                  <Can
                    role={hasPermission(user, "menu:queue-integration") ? "admin" : user.profile}
                    perform="dashboard:view"
                    yes={() => (
                      <ListItemLink
                        to="/queue-integration"
                        primary={i18n.t("mainDrawer.listItems.queueIntegration")}
                        icon={<DeviceHubOutlined />}
                        tooltip={collapsed}
                      />
                    )}
                  />
                )}
                {user.super && (
                  <ListItemLink
                    to="/allConnections"
                    primary={i18n.t("mainDrawer.listItems.allConnections")}
                    icon={<PhonelinkSetup />}
                    tooltip={collapsed}
                  />
                )}

                <ListSubheader disableSticky style={{ fontSize: 11, lineHeight: "28px" }}>Operacional</ListSubheader>
                <Can
                  role={hasPermission(user, "menu:sefaz-monitor") ? "admin" : user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/sefaz-monitor"
                      primary={i18n.t("mainDrawer.listItems.sefazMonitor")}
                      icon={<ReceiptOutlinedIcon />}
                      tooltip={collapsed}
                    />
                  )}
                />
                {showExternalApi && (
                  <Can
                    role={hasPermission(user, "menu:messages-api") ? "admin" : user.profile}
                    perform="dashboard:view"
                    yes={() => (
                      <ListItemLink
                        to="/messages-api"
                        primary={i18n.t("mainDrawer.listItems.messagesAPI")}
                        icon={<CodeRoundedIcon />}
                        tooltip={collapsed}
                      />
                    )}
                  />
                )}

                {user.super && (
                  <>
                    <ListSubheader disableSticky style={{ fontSize: 11, lineHeight: "28px" }}>Empresas</ListSubheader>
                    <ListItemLink
                      to="/companies"
                      primary={i18n.t("mainDrawer.listItems.companies")}
                      icon={<BusinessIcon />}
                      tooltip={collapsed}
                    />
                  </>
                )}
              </List>
            </Collapse>
          </>

        )}
      />
      {!collapsed && (
        <React.Fragment>
          <Divider />
          {/* // IMAGEM NO MENU
              <Hidden only={['sm', 'xs']}>
                <img style={{ width: "100%", padding: "10px" }} src={logo} alt="image" />            
              </Hidden> 
              */}
          <Typography
            style={{
              fontSize: "12px",
              padding: "10px",
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            {`V3.9.0`}
          </Typography>
        </React.Fragment>
      )}
    </div>
  );
};

export default MainListItems;